import { CalcModes, OrderTypes } from '../constants/trading.constants';
import { CalculationError, captureError } from './error.util';
import { log } from './logging.util';

export const makeFromNumberBuySell = (num) => ({
  [OrderTypes.Buy]: num,
  [OrderTypes.Sell]: num,
});

export const roundToStep = (num, volumeStep) =>
  Math.round(num * volumeStep) / volumeStep;

export const roundVolume = (volume, volumeStep) => {
  const volumeStepInversed = 1 / volumeStep;
  const res = Math.floor(volume * volumeStepInversed) / volumeStepInversed;
  if (res === 0) {
    return volumeStep;
  }
  return res;
};

export const getKOperation = (calcMode) => {
  switch (calcMode) {
    case CalcModes.FOREX_NO_LEVERAGE:
    case CalcModes.FOREX:
      return 10;
    case CalcModes.CFDLEVERAGE:
      return 20;
    case CalcModes.CFD:
      return 40;
    default:
      captureError(
        new CalculationError(`Cant find KOperation for calc_mode = ${calcMode}`)
      );
      return 0;
  }
};

export const calculateTrade = ({
  limitInvestRate,
  kRate,
  contractSize,
  calcMode,
  equity,
  marginRate,
  price: { ask, bid } = {},
  leverage,
}) => {
  const INITIAL = { [OrderTypes.Buy]: 0, [OrderTypes.Sell]: 0 };
  let volume = INITIAL;
  let margin = INITIAL;

  // for now, it's common for all calcModes
  margin = {
    [OrderTypes.Buy]: limitInvestRate * equity,
    [OrderTypes.Sell]: limitInvestRate * equity,
  };

  switch (calcMode) {
    case CalcModes.FOREX_NO_LEVERAGE:
      volume = {
        [OrderTypes.Buy]:
          (margin[OrderTypes.Buy] /
            ((contractSize * marginRate[OrderTypes.Buy] * (ask + bid)) / 2)) *
          kRate,
        [OrderTypes.Sell]:
          (margin[OrderTypes.Sell] /
            ((contractSize * marginRate[OrderTypes.Sell] * (ask + bid)) / 2)) *
          kRate,
      };
      break;

    case CalcModes.FOREX:
      volume = {
        [OrderTypes.Buy]:
          (margin[OrderTypes.Buy] /
            (((contractSize / leverage) *
              marginRate[OrderTypes.Buy] *
              (ask + bid)) /
              2)) *
          kRate,
        [OrderTypes.Sell]:
          (margin[OrderTypes.Sell] /
            (((contractSize / leverage) *
              marginRate[OrderTypes.Sell] *
              (ask + bid)) /
              2)) *
          kRate,
      };
      break;

    case CalcModes.CFD:
      volume = {
        [OrderTypes.Buy]:
          margin[OrderTypes.Buy] /
          (contractSize *
            ((ask + bid) / 2) *
            marginRate[OrderTypes.Buy] *
            kRate),
        [OrderTypes.Sell]:
          margin[OrderTypes.Sell] /
          (contractSize *
            ((ask + bid) / 2) *
            marginRate[OrderTypes.Sell] *
            kRate),
      };
      break;

    case CalcModes.CFDLEVERAGE:
      volume = {
        [OrderTypes.Buy]:
          (margin[OrderTypes.Buy] /
            ((contractSize * ((ask + bid) / 2)) / leverage)) *
          marginRate[OrderTypes.Buy] *
          kRate,
        [OrderTypes.Sell]:
          (margin[OrderTypes.Sell] /
            ((contractSize * ((ask + bid) / 2)) / leverage)) *
          marginRate[OrderTypes.Sell] *
          kRate,
      };
      break;

    default:
      captureError(new CalculationError(`Unknown calc_mode = ${calcMode}`));
  }
  return { volume, margin };
};

export const calculateMaxVolume = ({
  freeMargin,
  kRate,
  contractSize,
  calcMode,
  price: { ask, bid } = {},
  leverage = 1,
  volumeStep,
  marginRate,
  commission,
}) => {
  if (
    !freeMargin ||
    kRate === undefined ||
    !contractSize ||
    calcMode === undefined ||
    !ask ||
    !bid ||
    marginRate === undefined ||
    commission === undefined
  ) {
    log('Not enough data to calculate max volume', 'calculateMaxVolume');
    return {
      [OrderTypes.Buy]: 0,
      [OrderTypes.Sell]: 0,
    };
  }

  const maxVolumeWithoutLeverage = roundVolume(
    freeMargin / (contractSize * kRate * ask * marginRate[OrderTypes.Buy]),
    volumeStep
  );

  const maxVolumeWithLeverage = roundVolume(
    freeMargin /
      ((contractSize * marginRate[OrderTypes.Buy] * (kRate * ask)) / leverage),
    volumeStep
  );

  switch (calcMode) {
    case CalcModes.FOREX:
      return {
        [OrderTypes.Buy]: maxVolumeWithLeverage,
        [OrderTypes.Sell]: maxVolumeWithLeverage,
      };
    case CalcModes.CFD:
      return {
        [OrderTypes.Buy]: maxVolumeWithoutLeverage,
        [OrderTypes.Sell]: maxVolumeWithoutLeverage,
      };

    case CalcModes.CFDLEVERAGE:
      return {
        [OrderTypes.Buy]: maxVolumeWithLeverage,
        [OrderTypes.Sell]: maxVolumeWithLeverage,
      };
    default:
      captureError(new CalculationError(`Unknown calc_mode = ${calcMode}`));
      return { [OrderTypes.Buy]: 0, [OrderTypes.Sell]: 0 };
  }
};

export const calculateProfit = ({ profitFactor, calcMode, loss }) => {
  if (
    profitFactor === undefined ||
    loss === undefined ||
    calcMode === undefined
  ) {
    log('Not enough data to calculate profit', 'calculateProfit');
    return 0;
  }
  let res = {};
  switch (calcMode) {
    default:
      res = profitFactor * loss;
      break;
  }
  return res;
};

export const calculateLoss = ({ limitInvestRate, equity, calcMode }) => {
  if (
    limitInvestRate === undefined ||
    equity === undefined ||
    calcMode === undefined
  ) {
    log('Not enough data to calculate loss', 'calculateLoss');
    return 0;
  }
  let res = {};
  switch (calcMode) {
    default:
      res = equity * limitInvestRate;
      break;
  }
  return res;
};

export const calculateProfitFactorFromProfit = ({ profit, calcMode, loss }) => {
  if (profit === undefined || loss === undefined || calcMode === undefined) {
    log(
      'Not enough data to calculate profit factor',
      'calculateProfitFactorFromProfit'
    );
    return 0;
  }
  let res = 0;
  switch (calcMode) {
    default:
      res = profit / loss;
      break;
  }
  return res;
};

export const calculateMinLimitOfInvestRate = ({
  minVolume,
  contractSize,
  equity,
  calcMode,
  price,
  orderType,
  tickSize,
  kRate,
}) => {
  if (
    contractSize === undefined ||
    equity === undefined ||
    tickSize === undefined ||
    kRate === undefined ||
    !minVolume ||
    !price.bid ||
    !price.ask
  ) {
    log('Not enough arguments for calculateMinLimitOfInvestRate');
    return 0;
  }
  if (!orderType) orderType = OrderTypes.Buy;
  if (typeof minVolume === 'object') minVolume = minVolume[orderType];
  const kOperation = getKOperation(calcMode);
  let res = {};
  switch (calcMode) {
    case CalcModes.FOREX_NO_LEVERAGE:
    case CalcModes.FOREX:
    case CalcModes.CFD:
    case CalcModes.CFDLEVERAGE:
      res =
        (minVolume *
          kRate *
          kOperation *
          (price.ask - price.bid) *
          contractSize) /
        equity;
      break;
    default:
      captureError(new CalculationError(`Unknown calc_mode = ${calcMode}`));
  }
  return res;
};

const C_LOG = 1.6;

export const transpondSliderToNum = (value) => value ** C_LOG;

export const transpondNumToSlider = (value) => value ** (1 / C_LOG);

export const getValidConditionsForPendingOrders = (
  order,
  { price, digits, stopsLimit }
) => {
  let conditions = {};
  const tickSize = 1 / 10 ** (digits || 0);
  const k = 1.5;
  const { price_current, type } = order;
  const delta = stopsLimit * tickSize * k;
  switch (type) {
    case OrderTypes.BuyLimit:
      conditions = {
        price: {
          max: price_current - delta,
        },
        sl: {
          max: price,
        },
        tp: {
          min: price,
        },
      };
      break;
    case OrderTypes.SellLimit:
      conditions = {
        price: {
          min: price_current + delta,
        },
        sl: {
          min: price,
        },
        tp: {
          max: price,
        },
      };
      break;
    case OrderTypes.BuyStop:
      conditions = {
        price: {
          min: price_current + delta,
        },
        sl: {
          max: price,
        },
        tp: {
          min: price,
        },
      };
      break;
    case OrderTypes.SellStop:
      conditions = {
        price: {
          max: price_current - delta,
        },
        sl: {
          min: price,
        },
        tp: {
          max: price,
        },
      };
      break;
    default:
      break;
  }
  return conditions;
};

export const calculateProfitFromTakeProfit = ({
  volume,
  takeProfitLevel,
  openPrice,
  kRate,
  contractSize,
  commission,
}) => {
  return (
    volume *
    kRate *
    contractSize *
    Math.abs(openPrice - takeProfitLevel - commission)
  );
};

export const calculateLossFromStopLoss = ({
  volume,
  stopLossLevel,
  openPrice,
  kRate,
  contractSize,
  commission,
}) => {
  return (
    volume *
    kRate *
    contractSize *
    Math.abs(openPrice - stopLossLevel - commission)
  );
};

export const calculateProfitFromLevel = ({
  orderType,
  volume,
  contractSize,
  price,
  level,
  kRate,
  digits,
}) => {
  if (
    !orderType ||
    !volume ||
    !contractSize ||
    !price ||
    !level ||
    !kRate ||
    !digits
  ) {
    captureError(
      new CalculationError('Not enough arguments for calculateProfitFromLevel')
    );
  }

  if (
    orderType === OrderTypes.Buy ||
    orderType === OrderTypes.BuyLimit ||
    orderType === OrderTypes.BuyStop
  ) {
    if (level - price.bid < 0) return 0;
    return volume * contractSize * kRate * ((level - price.bid) / digits);
  }

  if (
    orderType === OrderTypes.Sell ||
    orderType === OrderTypes.SellLimit ||
    orderType === OrderTypes.SellStop
  ) {
    if (price.ask - level < 0) return 0;
    return volume * contractSize * kRate * ((price.ask - level) / digits);
  }

  return 0;
};

window.calculateProfit = calculateProfit;
window.calculateTrade = calculateTrade;
window.test = {};
window.calculateProfitFactorFromProfit = calculateProfitFactorFromProfit;
window.calculateMinLimitOfInvestRate = calculateMinLimitOfInvestRate;
window.test.profit = 10;
window.test.freeMargin = 210;
window.test.limitInvestRate = 0.15;
window.test.contractSize = 100000;
window.test.calcMode = 5;
window.test.volume = { 0: 0.01, 1: 0.01 };
window.test.price = { bid: 1.025, ask: 1.023 };
window.test.orderType = 0;
